import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { useAbortController } from '../hooks/useAbortController';
import type {
  ColDef,
  IDatasource,
  IGetRowsParams,
  GetRowIdParams,
  GridReadyEvent,
  CellMouseDownEvent,
  RowClickedEvent,
  FilterChangedEvent,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { useApiFetch } from '../hooks/useApiFetch';
import type { SampleListItem, SampleStatus } from '../types/samples';
import { STATUS_OPTIONS } from '../types/samples';
import { ErrorDisplay } from './ErrorDisplay';
import { Layout } from './Layout';
import {
  TimeAgoCellRenderer,
  NumberCellRenderer,
  DurationCellRenderer,
  CopyButtonCellRenderer,
  CopyButton,
  TimelineLinkCellRenderer,
} from './ag-grid/cellRenderers';
import { isCopyButtonClick } from './ag-grid/isCopyButtonClick';
import { isLinkClick } from './ag-grid/isLinkClick';
import './ag-grid/styles.css';
import { getSampleViewUrl } from '../utils/url';
import { localInputToIso, isoToLocalInput } from '../utils/datetime';
import { formatScores, formatScoresFull } from '../utils/formatScores';

ModuleRegistry.registerModules([AllCommunityModule]);

const PAGE_SIZE = 100;

// Maps AG Grid field names to backend filter_* query param names
const COLUMN_FILTER_PARAMS: Record<string, string> = {
  model: 'filter_model',
  created_by: 'filter_created_by',
  task_name: 'filter_task_name',
  task_version: 'filter_task_version',
  eval_set_id: 'filter_eval_set_id',
  error_message: 'filter_error_message',
  id: 'filter_id',
};

const TEXT_FILTER_DEF = {
  filter: 'agTextColumnFilter' as const,
  filterParams: {
    filterOptions: ['contains' as const],
    maxNumConditions: 1,
  },
};

function StatusCellRenderer({
  value,
  data,
}: {
  value: SampleStatus;
  data: SampleListItem;
}) {
  const statusClass =
    value === 'success'
      ? 'status-success'
      : value === 'error'
        ? 'status-error'
        : 'status-limit';

  const label = STATUS_OPTIONS.find(o => o.value === value)?.label || value;

  // Show error message preview for errors
  if (value === 'error' && data?.error_message) {
    return (
      <span className={statusClass} title={data.error_message}>
        Error
      </span>
    );
  }

  return <span className={statusClass}>{label}</span>;
}

function ScoreCellRenderer({ data }: { data?: SampleListItem }) {
  const scores = data?.scores ?? [];
  // The cell caps at three entries and clips long dict values; the tooltip
  // must not, or the hover reveals nothing the cell didn't already show.
  return <span title={formatScoresFull(scores)}>{formatScores(scores)}</span>;
}

function ErrorCellRenderer({ value }: { value: string | null }) {
  if (!value) return <span>-</span>;
  const preview = value.length > 100 ? value.slice(0, 100) + '...' : value;
  return (
    <span className="copy-button-cell">
      <span
        className="copy-button-cell-text text-red-600 dark:text-red-400"
        title={value}
      >
        {preview}
      </span>
      <CopyButton value={value} />
    </span>
  );
}

export function SampleList() {
  const { apiFetch, error: fetchError } = useApiFetch();
  const gridRef = useRef<AgGridReact<SampleListItem>>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params
  const [searchQuery, setSearchQuery] = useState(
    () => searchParams.get('search') || ''
  );
  const [statusFilter, setStatusFilter] = useState<SampleStatus | ''>(
    () => (searchParams.get('status') as SampleStatus) || ''
  );
  const [scoreMin, setScoreMin] = useState<string>(
    () => searchParams.get('score_min') || ''
  );
  const [scoreMax, setScoreMax] = useState<string>(
    () => searchParams.get('score_max') || ''
  );
  const [completedAfter, setCompletedAfter] = useState<string>(
    () => searchParams.get('completed_after') || ''
  );
  const [completedBefore, setCompletedBefore] = useState<string>(
    () => searchParams.get('completed_before') || ''
  );
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [columnFilterValues, setColumnFilterValues] = useState<
    Record<string, string>
  >(() => {
    const initial: Record<string, string> = {};
    for (const [, paramName] of Object.entries(COLUMN_FILTER_PARAMS)) {
      const value = searchParams.get(paramName);
      if (value) initial[paramName] = value;
    }
    return initial;
  });
  const { getAbortController } = useAbortController();

  // Sync URL with filter state
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery.trim()) params.set('search', searchQuery.trim());
    if (statusFilter) params.set('status', statusFilter);
    if (scoreMin) params.set('score_min', scoreMin);
    if (scoreMax) params.set('score_max', scoreMax);
    if (completedAfter) params.set('completed_after', completedAfter);
    if (completedBefore) params.set('completed_before', completedBefore);
    for (const [paramName, value] of Object.entries(columnFilterValues)) {
      params.set(paramName, value);
    }
    setSearchParams(params, { replace: true });
  }, [
    searchQuery,
    statusFilter,
    scoreMin,
    scoreMax,
    completedAfter,
    completedBefore,
    columnFilterValues,
    setSearchParams,
  ]);

  // Focus search input on mount
  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  // Create datasource for infinite row model
  const datasource = useMemo<IDatasource>(() => {
    return {
      rowCount: undefined,
      getRows: async (params: IGetRowsParams) => {
        const abortController = getAbortController();
        setIsLoading(true);

        const page = Math.floor(params.startRow / PAGE_SIZE) + 1;

        const queryParams = new URLSearchParams({
          page: page.toString(),
          limit: PAGE_SIZE.toString(),
        });

        if (searchQuery.trim()) {
          queryParams.set('search', searchQuery.trim());
        }

        if (statusFilter) {
          queryParams.append('status', statusFilter);
        }

        const scoreMinNum = parseFloat(scoreMin);
        const scoreMaxNum = parseFloat(scoreMax);
        if (!isNaN(scoreMinNum)) {
          queryParams.set('score_min', scoreMinNum.toString());
        }
        if (!isNaN(scoreMaxNum)) {
          queryParams.set('score_max', scoreMaxNum.toString());
        }

        // completedAfter/completedBefore are already absolute UTC ISO instants.
        if (completedAfter) {
          queryParams.set('completed_after', completedAfter);
        }
        if (completedBefore) {
          queryParams.set('completed_before', completedBefore);
        }

        // Handle column filters from AG Grid filter model
        const filterModel = params.filterModel;
        if (filterModel) {
          for (const [field, paramName] of Object.entries(
            COLUMN_FILTER_PARAMS
          )) {
            const value = filterModel[field]?.filter;
            if (value) {
              queryParams.set(paramName, value);
            }
          }
        }

        // Handle sorting
        const sortModel = params.sortModel;
        if (sortModel && sortModel.length > 0) {
          queryParams.set('sort_by', sortModel[0].colId);
          queryParams.set('sort_order', sortModel[0].sort ?? 'desc');
        }

        try {
          const response = await apiFetch(`/meta/samples?${queryParams}`, {
            signal: abortController.signal,
          });

          // If this request was aborted, don't process the response
          if (abortController.signal.aborted) {
            console.log('Sample list request was cancelled');
            return;
          }

          setIsLoading(false);

          if (!response) {
            params.failCallback();
            return;
          }

          const data = await response.json();
          // For infinite model, lastRow tells the grid when we've reached the end.
          // Use has_more to determine if there are additional pages.
          const lastRow = data.has_more
            ? -1
            : params.startRow + data.items.length;
          params.successCallback(data.items, lastRow);
          setHasLoaded(true);
        } catch (error) {
          // Don't update state if request was aborted
          if (abortController.signal.aborted) {
            console.log('Sample list request was cancelled');
            return;
          }
          console.error('Sample list fetch failed:', error);
          setIsLoading(false);
          params.failCallback();
        }
      },
    };
  }, [
    apiFetch,
    searchQuery,
    statusFilter,
    scoreMin,
    scoreMax,
    completedAfter,
    completedBefore,
    getAbortController,
  ]);

  // Column definitions
  const columnDefs = useMemo<ColDef<SampleListItem>[]>(
    () => [
      {
        field: 'eval_set_id',
        headerName: 'Eval Set',
        width: 180,
        pinned: 'left',
        cellRenderer: CopyButtonCellRenderer,
        ...TEXT_FILTER_DEF,
      },
      {
        field: 'task_name',
        headerName: 'Task',
        width: 160,
        ...TEXT_FILTER_DEF,
      },
      {
        field: 'task_version',
        headerName: 'Task Version',
        width: 170,
        valueFormatter: params => params.value || '-',
        ...TEXT_FILTER_DEF,
      },
      {
        field: 'id',
        headerName: 'ID',
        width: 80,
        ...TEXT_FILTER_DEF,
      },
      {
        field: 'model',
        headerName: 'Model',
        width: 180,
        ...TEXT_FILTER_DEF,
      },
      {
        field: 'created_by',
        headerName: 'Author',
        width: 120,
        valueFormatter: params => params.value || '-',
        ...TEXT_FILTER_DEF,
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 90,
        cellRenderer: StatusCellRenderer,
      },
      {
        // field stays score_value: the grid sends colId as sort_by (see below),
        // so renaming it would break server-side sorting. The renderer reads
        // data.scores instead of the field value.
        field: 'score_value',
        headerName: 'Score',
        width: 200,
        cellRenderer: ScoreCellRenderer,
      },
      {
        field: 'input_tokens',
        headerName: 'In Tokens',
        width: 90,
        cellRenderer: NumberCellRenderer,
      },
      {
        field: 'output_tokens',
        headerName: 'Out Tokens',
        width: 95,
        cellRenderer: NumberCellRenderer,
      },
      {
        field: 'total_tokens',
        headerName: 'Total Tokens',
        width: 100,
        cellRenderer: NumberCellRenderer,
      },
      {
        field: 'message_count',
        headerName: 'Messages',
        width: 90,
        cellRenderer: NumberCellRenderer,
      },
      {
        field: 'action_count',
        headerName: 'Actions',
        width: 80,
        cellRenderer: NumberCellRenderer,
      },
      {
        field: 'total_time_seconds',
        headerName: 'Duration',
        width: 90,
        cellRenderer: DurationCellRenderer,
      },
      {
        colId: 'timeline',
        headerName: 'Timeline',
        width: 90,
        cellRenderer: TimelineLinkCellRenderer,
        sortable: false,
        filter: false,
      },
      {
        field: 'completed_at',
        headerName: 'Completed',
        width: 110,
        cellRenderer: TimeAgoCellRenderer,
        sort: 'desc',
      },
      {
        field: 'uuid',
        headerName: 'UUID',
        width: 290,
        cellRenderer: CopyButtonCellRenderer,
      },
      {
        field: 'eval_id',
        headerName: 'Eval ID',
        width: 200,
        hide: true,
      },
      {
        field: 'reasoning_tokens',
        headerName: 'Reasoning Tokens',
        width: 130,
        cellRenderer: NumberCellRenderer,
        hide: true,
      },
      {
        field: 'working_time_seconds',
        headerName: 'Working Time',
        width: 110,
        cellRenderer: DurationCellRenderer,
        hide: true,
      },
      {
        field: 'generation_time_seconds',
        headerName: 'Gen Time',
        width: 90,
        cellRenderer: DurationCellRenderer,
        hide: true,
      },
      {
        field: 'error_message',
        headerName: 'Error',
        width: 300,
        cellRenderer: ErrorCellRenderer,
        ...TEXT_FILTER_DEF,
      },
      {
        field: 'is_invalid',
        headerName: 'Invalid',
        width: 70,
      },
      {
        field: 'score_scorer',
        headerName: 'Scorer',
        width: 100,
        hide: true,
      },
      {
        field: 'location',
        headerName: 'Location',
        width: 300,
        hide: true,
      },
    ],
    []
  );

  const defaultColDef = useMemo<ColDef<SampleListItem>>(
    () => ({
      sortable: true,
      resizable: true,
      filter: false,
      minWidth: 80,
    }),
    []
  );

  const getRowId = useCallback(
    (params: GetRowIdParams<SampleListItem>) => params.data.uuid,
    []
  );

  const handleRowClicked = useCallback(
    (event: RowClickedEvent<SampleListItem>) => {
      if (isCopyButtonClick(event) || isLinkClick(event)) return;
      const mouseEvent = event.event as MouseEvent | undefined;
      if (
        mouseEvent?.button === 1 ||
        mouseEvent?.ctrlKey ||
        mouseEvent?.metaKey
      ) {
        return;
      }
      const sample = event.data;
      if (!sample) return;
      const { eval_set_id, filename, id, epoch } = sample;
      const url = getSampleViewUrl({
        evalSetId: eval_set_id,
        filename,
        sampleId: id,
        epoch,
      });
      window.location.href = url;
    },
    []
  );

  const handleCellMouseDown = useCallback(
    (event: CellMouseDownEvent<SampleListItem>) => {
      if (isCopyButtonClick(event) || isLinkClick(event)) return;
      const mouseEvent = event.event as MouseEvent;
      if (mouseEvent.button === 1 || mouseEvent.ctrlKey || mouseEvent.metaKey) {
        const sample = event.data;
        if (!sample) return;
        const { eval_set_id, filename, id, epoch } = sample;
        const url = getSampleViewUrl({
          evalSetId: eval_set_id,
          filename,
          sampleId: id,
          epoch,
        });
        window.open(url, '_blank');
      }
    },
    []
  );

  const onFilterChanged = useCallback(
    (_event: FilterChangedEvent<SampleListItem>) => {
      const model = gridRef.current?.api?.getFilterModel();
      const newValues: Record<string, string> = {};
      if (model) {
        for (const [field, paramName] of Object.entries(COLUMN_FILTER_PARAMS)) {
          const value = model[field]?.filter;
          if (value) newValues[paramName] = value;
        }
      }
      setColumnFilterValues(newValues);
    },
    []
  );

  const onGridReady = useCallback(
    (params: GridReadyEvent<SampleListItem>) => {
      // Restore column filters from URL params
      const initialFilterModel: Record<
        string,
        { type: string; filter: string }
      > = {};
      for (const [field, paramName] of Object.entries(COLUMN_FILTER_PARAMS)) {
        const value = searchParams.get(paramName);
        if (value) {
          initialFilterModel[field] = { type: 'contains', filter: value };
        }
      }
      if (Object.keys(initialFilterModel).length > 0) {
        params.api.setFilterModel(initialFilterModel);
      }
      params.api.setGridOption('datasource', datasource);
    },
    [datasource, searchParams]
  );

  // Refresh grid when datasource changes
  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.setGridOption('datasource', datasource);
      gridRef.current.api.purgeInfiniteCache();
    }
  }, [datasource]);

  const handleStatusChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setStatusFilter(e.target.value as SampleStatus | '');
    },
    []
  );

  const clearFilters = useCallback(() => {
    setSearchQuery('');
    setStatusFilter('');
    setScoreMin('');
    setScoreMax('');
    setCompletedAfter('');
    setCompletedBefore('');
    gridRef.current?.api?.setFilterModel(null);
    setColumnFilterValues({});
  }, []);

  const handleRefresh = useCallback(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.purgeInfiniteCache();
    }
  }, []);

  const hasColumnFilters = Object.keys(columnFilterValues).length > 0;
  const hasFilters =
    searchQuery ||
    statusFilter ||
    scoreMin ||
    scoreMax ||
    completedAfter ||
    completedBefore ||
    hasColumnFilters;

  if (fetchError) {
    return (
      <Layout>
        <ErrorDisplay message={fetchError.toString()} />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="h-full flex flex-col overflow-hidden bg-white dark:bg-gray-900">
        <div className="bg-gray-50 dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800 px-4 py-2 shrink-0">
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0 overflow-x-auto">
              <div className="flex items-center gap-3 min-w-max py-px">
                <div className="relative flex-1 min-w-56 max-w-md">
                  <input
                    ref={searchInputRef}
                    type="search"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full h-8 px-3 text-sm rounded border border-gray-300 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-brand-600 focus:border-brand-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:placeholder:text-gray-500 dark:focus:ring-brand-300 dark:focus:border-brand-300"
                  />
                  {isLoading && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2">
                      <div className="animate-spin h-4 w-4 border-2 border-gray-300 dark:border-gray-700 border-t-brand-600 dark:border-t-brand-300 rounded-full"></div>
                    </div>
                  )}
                </div>

                <select
                  id="status-filter"
                  value={statusFilter}
                  onChange={handleStatusChange}
                  className="shrink-0 h-8 px-2 text-sm rounded border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-brand-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:focus:ring-brand-300"
                >
                  <option value="">All Status</option>
                  {STATUS_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>

                <div
                  className="flex items-center gap-1 shrink-0"
                  title="Filters on a sample's single final score. Samples with several final scores are excluded, as are samples still running."
                >
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Score:
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Min"
                    value={scoreMin}
                    onChange={e => setScoreMin(e.target.value)}
                    className="w-16 h-8 px-2 text-sm rounded border border-gray-300 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-brand-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:placeholder:text-gray-500 dark:focus:ring-brand-300"
                  />
                  <span className="text-gray-400 dark:text-gray-500">–</span>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Max"
                    value={scoreMax}
                    onChange={e => setScoreMax(e.target.value)}
                    className="w-16 h-8 px-2 text-sm rounded border border-gray-300 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-brand-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:placeholder:text-gray-500 dark:focus:ring-brand-300"
                  />
                </div>

                <div className="flex items-center gap-1 shrink-0">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Completed:
                  </span>
                  <input
                    type="datetime-local"
                    aria-label="Completed after"
                    title="Completed after"
                    value={isoToLocalInput(completedAfter)}
                    max={isoToLocalInput(completedBefore) || undefined}
                    onChange={e =>
                      setCompletedAfter(localInputToIso(e.target.value) ?? '')
                    }
                    className="h-8 px-2 text-sm rounded border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-brand-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:focus:ring-brand-300"
                  />
                  <span className="text-gray-400 dark:text-gray-500">–</span>
                  <input
                    type="datetime-local"
                    aria-label="Completed before"
                    title="Completed before"
                    value={isoToLocalInput(completedBefore)}
                    min={isoToLocalInput(completedAfter) || undefined}
                    onChange={e =>
                      setCompletedBefore(localInputToIso(e.target.value) ?? '')
                    }
                    className="h-8 px-2 text-sm rounded border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-brand-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:focus:ring-brand-300"
                  />
                </div>

                {hasFilters && (
                  <button
                    onClick={clearFilters}
                    className="shrink-0 h-8 px-3 text-xs rounded border border-gray-300 bg-white text-gray-600 hover:text-gray-900 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="shrink-0 h-8 px-3 text-xs rounded border border-gray-300 bg-white text-gray-600 hover:text-gray-900 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100"
              title="Refresh results"
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden relative">
          {!hasLoaded && (
            <div className="absolute inset-0 bg-white dark:bg-gray-900 z-10 p-4">
              <div className="space-y-2">
                {Array.from({ length: 15 }).map((_, i) => (
                  <div key={i} className="flex gap-4 animate-pulse">
                    <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-40"></div>
                    <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-32"></div>
                    <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-16"></div>
                    <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-36"></div>
                    <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-24"></div>
                    <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-20"></div>
                    <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-16"></div>
                    <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded flex-1"></div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="ag-theme-quartz h-full w-full">
            <AgGridReact<SampleListItem>
              ref={gridRef}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowModelType="infinite"
              onGridReady={onGridReady}
              onFilterChanged={onFilterChanged}
              onRowClicked={handleRowClicked}
              onCellMouseDown={handleCellMouseDown}
              cacheBlockSize={PAGE_SIZE}
              cacheOverflowSize={2}
              maxConcurrentDatasourceRequests={1}
              infiniteInitialRowCount={PAGE_SIZE}
              maxBlocksInCache={10}
              getRowId={getRowId}
              animateRows={false}
              suppressCellFocus={true}
            />
          </div>
        </div>
      </div>
    </Layout>
  );
}
